import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CACHE_TIME, PAGINATION } from '@hygge/shared';
import type { BulkTaskResult, TaskFilters } from '@hygge/shared';
import { getAllIssues } from '@/lib/all-issues';
import { bulkTaskAction, type BulkTaskPayload } from '@/lib/bulk-tasks';
import { useBoards } from '@/hooks/use-boards';
import { useAuthStore } from '@/stores/auth';

export const allIssuesKeys = {
  all: ['all-issues'] as const,
  list: (filters?: TaskFilters & { page?: number; limit?: number }) =>
    [...allIssuesKeys.all, filters] as const,
};

/**
 * How many pooled issues we load in one page. Client-side filters operate over
 * this loaded set, so a larger page reduces truncation — but the response's
 * `meta.total` is the honest total and the view flags when it exceeds this cap.
 * Full server-side filtering + pagination is a documented follow-up.
 *
 * Pinned to the server's cap (`PaginationQueryDto` rejects `limit > MAX_LIMIT`
 * with a 400), so this can't drift above what the API accepts.
 */
export const ALL_ISSUES_PAGE_SIZE = PAGINATION.MAX_LIMIT;

export function useAllIssues(
  filters?: TaskFilters & { page?: number; limit?: number; sort?: string; order?: string },
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: allIssuesKeys.list(filters),
    queryFn: () => getAllIssues({ ...filters, limit: ALL_ISSUES_PAGE_SIZE }),
    staleTime: CACHE_TIME.SHORT,
    // Callers filtering by assignee must be able to wait for the id. Without
    // this, `assigneeId: undefined` is not "nobody yet" — it is no filter at
    // all, and the query cheerfully returns the entire workspace, which a
    // caller counting "your overdue work" would then present as the reader's.
    enabled: options?.enabled ?? true,
  });
}

/**
 * Cross-board bulk action. The per-board bulk endpoint (`POST
 * /boards/:boardId/tasks/bulk`) only accepts keys belonging to that one board,
 * so a cross-board selection is split by its SOURCE board and fired one bulk
 * call per board — never sending a board's keys to another board's endpoint.
 * `move_board` also drops keys already on the target board (a same-board move is
 * rejected by the endpoint). Results are aggregated.
 */
export function useCrossBoardBulkAction() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      keys: string[];
      /** taskKey → its owning board id, from the loaded All-issues rows. */
      keyToBoardId: Map<string, string>;
      payload: Omit<BulkTaskPayload, 'taskKeys'>;
    }): Promise<BulkTaskResult> => {
      const groups = new Map<string, string[]>();
      for (const key of input.keys) {
        const boardId = input.keyToBoardId.get(key);
        if (!boardId) continue;
        // A move to a board a task is already on is a no-op the endpoint rejects.
        if (input.payload.action === 'move_board' && input.payload.targetBoardId === boardId) {
          continue;
        }
        const bucket = groups.get(boardId);
        if (bucket) bucket.push(key);
        else groups.set(boardId, [key]);
      }

      // allSettled (not all): one board's request throwing (500/403/network) must
      // not discard the already-committed results from the other boards. A whole
      // rejected request folds its keys into `failed` with the error message;
      // per-task failures within a board are already carried in `res.failed`.
      const entries = [...groups.entries()];
      const settled = await Promise.allSettled(
        entries.map(([boardId, taskKeys]) => bulkTaskAction(boardId, { taskKeys, ...input.payload })),
      );

      let affected = 0;
      const failed: BulkTaskResult['failed'] = [];
      settled.forEach((result, i) => {
        const [, taskKeys] = entries[i];
        if (result.status === 'fulfilled') {
          affected += result.value.affected;
          failed.push(...result.value.failed);
        } else {
          const reason =
            result.reason instanceof Error ? result.reason.message : 'Request failed';
          for (const key of taskKeys) failed.push({ key, reason });
        }
      });

      return { affected, failed };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: allIssuesKeys.all });
      // Per-board lists, sprint/backlog groupings and overviews can all shift.
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['sprints'] });
      qc.invalidateQueries({ queryKey: ['backlog'] });
      qc.invalidateQueries({ queryKey: ['board-overview'] });
    },
  });
}

/**
 * Tasks a link may point to: searched across every board the caller can see,
 * kept to the boards where they may change tasks (`task.manage`). A link is
 * written on both ends and shows each task to the other side, so the far board
 * asks for the same capability as the near one; viewing a board is not enough
 * (HW-909). The server applies the same rule when the link is created.
 */
export function useSearchLinkableTasks(query: string, excludeKey?: string) {
  const { data: boards, isLoading: isLoadingBoards } = useBoards();
  const linkable = (boards ?? [])
    .filter((b) => b.currentUserPermissions?.includes('task.manage'))
    .map((b) => b.id)
    .sort();
  // The boards go to the server, so the page holds only tasks that can be
  // linked; filtering a page of everything afterwards let boards the caller
  // can merely view crowd the linkable ones out.
  // Always sent, save for a super_admin, who may link to every board there is
  // and would otherwise put the whole estate in the URL. The cached list can
  // lag behind the server's: a board made since, which this caller may only
  // view, would be searched if the list were left out whenever it covered
  // every board cached.
  const isSuperAdmin = useAuthStore((s) => s.user?.role === 'super_admin');
  const boardIds = isSuperAdmin ? undefined : linkable.join(',');
  const search = useQuery({
    queryKey: [...allIssuesKeys.all, 'linkable-search', query, boardIds ?? 'all'],
    queryFn: () => getAllIssues({ search: query, limit: 21, ...(boardIds && { boardIds }) }),
    enabled: query.length >= 1 && linkable.length > 0,
    staleTime: CACHE_TIME.SHORT,
  });
  // One extra row asked for, so the task the card is on never costs a slot.
  const data =
    query.length >= 1 && !isLoadingBoards && linkable.length === 0
      ? [] // no board to link to: an answer, not an endless wait
      : search.data?.data.filter((t) => t.key !== excludeKey).slice(0, 20);
  // While the boards are still loading the answer is not known yet.
  return { ...search, data, isLoading: search.isLoading || (query.length >= 1 && isLoadingBoards) };
}
